import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { opsService } from '../../services/opsService';

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  trialing: 'bg-amber-100 text-amber-700 border-amber-200',
  past_due: 'bg-red-100 text-red-700 border-red-200',
  unpaid: 'bg-red-100 text-red-700 border-red-200',
  canceled: 'bg-red-50 text-red-600 border-red-100',
  inactive: 'bg-gray-100 text-gray-600 border-gray-200',
};

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

const Section = ({ title, children, right }) => (
  <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      {right}
    </div>
    <div>{children}</div>
  </section>
);

const Field = ({ label, value, mono }) => (
  <div>
    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">{label}</p>
    <p className={`text-sm text-slate-900 ${mono ? 'font-mono break-all' : ''}`}>{value || '—'}</p>
  </div>
);

const OpsCompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const res = await opsService.getCompanyDetail(companyId);
      if (cancelled) return;
      if (res?.error) setError(res.error.message || 'Failed to load company');
      else if (res?.data?.error) setError(res.data.error);
      else setData(res?.data || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const company = data?.company;
  const sub = data?.subscription;
  const sites = data?.sites || [];
  const users = data?.users || [];
  const activities = data?.activities || [];

  return (
    <>
      <Helmet><title>{company?.companyName ? `${company.companyName} · Ops` : 'Ops Dashboard'}</title></Helmet>
      <div className="min-h-screen bg-slate-50">
        <header className="bg-[#061a2b] text-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => navigate('/ops')}
                  className="text-xs text-slate-300 hover:text-white"
                >
                  ← Back to customers
                </button>
                <span className="text-slate-600">·</span>
                <Link
                  to="/admin-dashboard"
                  className="text-xs text-slate-300 hover:text-white transition-colors"
                >
                  Back to SiteProof
                </Link>
              </div>
              <h1 className="text-2xl font-bold">{company?.companyName || 'Company detail'}</h1>
              {company?.id && <p className="text-xs text-slate-400 mt-1 font-mono">{company.id}</p>}
            </div>
            {sub?.status && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${STATUS_BADGE[sub.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                {sub.status}
              </span>
            )}
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          {loading && (
            <div className="text-sm text-slate-500 py-8 text-center">Loading company detail…</div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Company Info */}
              <Section title="Company Info">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 px-5 py-5">
                  <Field label="Name" value={company?.companyName} />
                  <Field label="Contact email" value={company?.contactEmail} />
                  <Field label="Contact phone" value={company?.contactPhone} />
                  <Field label="Address" value={[company?.addressLine1, company?.city, company?.postcode, company?.country].filter(Boolean).join(', ')} />
                  <Field label="Created" value={fmtDate(company?.createdAt)} />
                  <Field label="Last updated" value={fmtDate(company?.updatedAt)} />
                  <Field label="Admin user id" value={company?.adminUserId} mono />
                </div>
              </Section>

              {/* Subscription */}
              <Section title="Subscription">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 px-5 py-5">
                  <Field label="Status" value={sub?.status} />
                  <Field label="Billing interval" value={sub?.billingInterval} />
                  <Field label="Billing cycle" value={sub?.billingCycle} />
                  <Field label="Active sites" value={sub?.activeSiteCount} />
                  <Field label="Estimated monthly value" value={`${fmtGBP(sub?.estimatedMonthlyValue ?? 0)} (est.)`} />
                  <Field label="Cancel at period end" value={sub?.cancelAtPeriodEnd ? 'Yes' : 'No'} />
                  <Field label="Current period end" value={fmtDate(sub?.currentPeriodEnd)} />
                  <Field label="Trial start" value={fmtDate(sub?.trialStartDate)} />
                  <Field label="Trial end" value={fmtDate(sub?.trialEndDate)} />
                  <Field label="Stripe customer" value={sub?.stripeCustomerId} mono />
                  <Field label="Stripe subscription" value={sub?.stripeSubscriptionId} mono />
                </div>
              </Section>

              {/* Sites */}
              <Section title={`Sites (${sites.length})`}>
                {sites.length === 0 ? (
                  <p className="px-5 py-5 text-sm text-slate-500">No sites</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-5 py-3 font-semibold">Site</th>
                          <th className="text-left px-5 py-3 font-semibold">Status</th>
                          <th className="text-left px-5 py-3 font-semibold">Address</th>
                          <th className="text-left px-5 py-3 font-semibold">Start</th>
                          <th className="text-left px-5 py-3 font-semibold">End</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sites.map((s) => (
                          <tr key={s.id}>
                            <td className="px-5 py-3 font-medium text-slate-900">{s.siteName || '—'}</td>
                            <td className="px-5 py-3 text-slate-600 capitalize">{s.status || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">{[s.addressLine1, s.city, s.postcode].filter(Boolean).join(', ') || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">{fmtDate(s.startDate)}</td>
                            <td className="px-5 py-3 text-slate-600">{fmtDate(s.endDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* Users */}
              <Section title={`Users (${users.length})`}>
                {users.length === 0 ? (
                  <p className="px-5 py-5 text-sm text-slate-500">No users</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-5 py-3 font-semibold">Name</th>
                          <th className="text-left px-5 py-3 font-semibold">Email</th>
                          <th className="text-left px-5 py-3 font-semibold">Role</th>
                          <th className="text-left px-5 py-3 font-semibold">Active</th>
                          <th className="text-left px-5 py-3 font-semibold">Joined</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {users.map((u) => (
                          <tr key={u.id}>
                            <td className="px-5 py-3 font-medium text-slate-900">{u.fullName || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">{u.email || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">{u.role || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">{u.isActive ? 'Yes' : 'No'}</td>
                            <td className="px-5 py-3 text-slate-600">{fmtDate(u.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* Recent activity */}
              <Section title={`Recent Activity (${activities.length})`}>
                {activities.length === 0 ? (
                  <p className="px-5 py-5 text-sm text-slate-500">No activity recorded</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {activities.map((a) => (
                      <li key={a.id} className="px-5 py-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900">{a.title || a.activityType}</p>
                            {a.description && <p className="text-xs text-slate-500 mt-0.5">{a.description}</p>}
                            <p className="text-[11px] text-slate-400 mt-1 font-mono">{a.activityType}</p>
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(a.createdAt)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default OpsCompanyDetail;
